import { mutationAtomWithLoadable } from 'lib/atoms';
import request from 'lib/request';
import type { PriceDeckPreviewResponse } from '../types';
import type { PriceDeckFormMode, PriceDeckFormValues } from './types';

// ─────────────────────────────────────
// Save Price Deck
// ─────────────────────────────────────

interface SavePriceDeckParams {
  payload: Record<string, unknown>;
  mode: PriceDeckFormMode;
  deckId: string | null;
}

/**
 * Save price deck mutation — creates (POST) or updates (PATCH) based on mode.
 * @param {SavePriceDeckParams} params - Payload, mode, and deckId.
 * @return {[Atom, Atom<Loadable>]} Base and loadable atoms.
 */
export const [savePriceDeckAtom, savePriceDeckLoadableAtom] =
  mutationAtomWithLoadable<unknown, [SavePriceDeckParams]>(
    async (_get, _set, { payload, mode, deckId }: SavePriceDeckParams) => {
      const url = mode === 'edit' ? `/price_decks/${deckId}` : '/price_decks';
      const method = mode === 'edit' ? 'patch' : 'post';
      const response = await request[method]<unknown, unknown>(
        url,
        payload,
        new AbortController()
      );
      if (response.type === 'success') return response.data;
      if (response.type === 'error') {
        const errorData = response.data as
          { errors?: Array<{ message: string }> } | undefined;
        const messages = errorData?.errors
          ? errorData.errors.map((e) => e.message).join(', ')
          : 'Validation failed';
        throw new Error(messages);
      }
      throw new Error('Failed to save price deck');
    }
  );

// ─────────────────────────────────────
// Preview Price Deck
// ─────────────────────────────────────

/**
 * Preview price deck mutation — calls eval_calculator to generate schedule + stats.
 * Triggered explicitly from the form when pricing-relevant fields change.
 * @param {PriceDeckFormValues} values - Current form values.
 * @return {[Atom, Atom<Loadable<PriceDeckPreviewResponse>>]} Base and loadable atoms.
 */
export const [previewPriceDeckAtom, previewPriceDeckLoadableAtom] =
  mutationAtomWithLoadable<PriceDeckPreviewResponse, [PriceDeckFormValues]>(
    async (_get, _set, values: PriceDeckFormValues) => {
      const controller = new AbortController();
      const response = await request.post<PriceDeckPreviewResponse, unknown>(
        '/price_decks/preview',
        { priceDeck: values },
        controller
      );
      if (response.type === 'success') return response.data;
      if (response.type === 'error') {
        const errorData = response.data as { detail?: string } | undefined;
        throw new Error(
          errorData?.detail ?? 'No strip prices found for this configuration.'
        );
      }
      throw new Error('No strip prices found for this configuration.');
    }
  );

// ─────────────────────────────────────
// Strip Reference (raw strip prices for modified strip dotted lines)
// ─────────────────────────────────────

/**
 * Fetch unscaled strip prices via eval_calculator preview with method_type='strip'.
 * Provides the full 360-month projection with fallback, used as dotted reference
 * lines when viewing modified strip pricing.
 */
export const [stripReferenceAtom, stripReferenceLoadableAtom] =
  mutationAtomWithLoadable<PriceDeckPreviewResponse, [PriceDeckFormValues]>(
    async (_get, _set, values: PriceDeckFormValues) => {
      const controller = new AbortController();
      const response = await request.post<PriceDeckPreviewResponse, unknown>(
        '/price_decks/preview',
        {
          priceDeck: {
            ...values,
            methodType: 'strip',
            noFillForward: true,
          },
        },
        controller
      );
      if (response.type === 'success') return response.data;
      throw new Error('Failed to fetch strip reference prices.');
    }
  );

// ─────────────────────────────────────
// Upload Price Deck File
// ─────────────────────────────────────

// Keys are snake_case because the upload endpoint uses `render json:` (not Jbuilder),
// so Rails does not auto-convert to camelCase.
interface UploadResult {
  blob_path: string;
  original_filename: string;
  row_count: number;
}

/**
 * Upload a price deck file to Azure via the Insights upload endpoint.
 * Uses raw fetch because the request lib doesn't support FormData.
 * @param {File} file - The file to upload.
 * @return {[Atom, Atom<Loadable<UploadResult>>]} Base and loadable atoms.
 */
export const [uploadPriceDeckFileAtom, uploadPriceDeckFileLoadableAtom] =
  mutationAtomWithLoadable<UploadResult, [File]>(
    async (_get, _set, file: File) => {
      const formData = new FormData();
      formData.append('file', file);

      const csrfToken =
        document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')
          ?.content ?? '';

      const res = await fetch('/price_decks/upload', {
        method: 'POST',
        headers: { 'X-CSRF-TOKEN': csrfToken },
        body: formData,
      });

      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.error ?? 'Upload failed');
      }
      return data as UploadResult;
    }
  );
